import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik';
import Header from '../Header/Header';
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTheme } from "../../ui/src/contextProviders"
import { Name, Role, DOB, LICENSE_NUMBER, SIGNATURE, DAY, FROM_TIME, TO_TIME, phoneNumberRegex, CONTACT_NUMBER, EMAIL, SPECIALITY } from "./utilities/schema"
import { Icon, Select, SuccessModal, Loader, ConfirmationPopup, ICONS, SignaturePad } from '@ohif/ui';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import changePasswordApi from '../rtk-apis/user/changePassword';
import { AppDispatch, RootState } from '../store/store';
import { useNavigate, useLocation } from 'react-router-dom';
import updateProfileApi from '../rtk-apis/user/updateProfie';
import { resetUpdateProfileStatus } from '@custom/store/slices/user/updateProfile';
import { resetChangePasswordStatus } from '@custom/store/slices/user/changePassword';
import * as Yup from 'yup';
import './profile.css';
import { capitalizeWords, customStyle, formatDate, formatDays, formatTime, getCurrencyContact } from '@custom/Utils/utils';
import updateProfileImage from '@custom/rtk-apis/report/updateProfileImage';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import dayjs from 'dayjs';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';
import { _roleType } from '@custom/Utils/constants/appConstants';

const Profile = () => {
    const [image, setImage] = useState('');
    const [countryCode, setCountryCode] = useState(['+91']); // Default country code
    const [roleList, setRoleList] = useState([])
    const [department, setDepartment] = useState([])
    const [specialitites, setSpecialitities] = useState([]);
    const [preview, setPreview] = useState(''); // State to store image preview
    const [errorMessage, setErrorMessage] = useState(''); // State to store error message
    const [successModal, setSuccessModal] = useState(false); // State to manage success modal visibility
    const [title, setTitle] = useState('');
    const [days, setDays] = useState([]);
    const [confirmationModal, setConfirmationModal] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");
    const imageResponse = useSelector((state: RootState) => state.updateProfileImageResponse);
    const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
    const [profileImage, setProfileImage] = useState<string>('');
    const [signatureImage, setSignatureImage] = useState<string>('');
    const [folderName, setFolderName] = useState<string>('');
    const navigate = useNavigate();
    const location = useLocation();
    const {isDarkMode} = useTheme();
    const editableRef = useRef(null);
    const editableNameRef = useRef(null);
    const { userDetail } = location?.state || {};
    const [isOpenSignaturePad, setOpenSignaturePad] = useState(false);
    const [genderOptions, setGenderOption] = useState([]);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+91');
    const hospitalName = sessionStorage.getItem('hospitalName') || localStorage.getItem('hospitalName');
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');
    const defaultCountryCode = billingGeneralSettingsData?.status && billingGeneralSettingsData?.result?.currency ? getCurrencyContact(billingGeneralSettingsData.result.currency) : '+91';
    const [formInitialValue, setFormInitialForm] = useState({
        name: userDetail?.name || '',
        profilePic: userDetail?.profilePic || '',
        hospitalLogo: userDetail?.hospitalDetail?.hospitalLogo || '',
        contactNumberCountryCode: defaultCountryCode,
        whatsappCountryCode: defaultCountryCode,
        empId: userDetail?.empId || '',
        role: userDetail?.role || '',
        hospitalName: hospitalName || '',
        hprId: userDetail?.hprId || '',
        gender: userDetail?.gender || '',
        email: userDetail?.email || '',
        contactNumber: userDetail?.contactNumber || '',
        dob: dayjs(userDetail?.dob) || '',
        department: userDetail?.department || '',
        specialization: userDetail?.specialization || [],
        yearsOfExperience: userDetail?.yearsOfExperience || '',
        whatsappNumber: userDetail?.whatsappNumber || '',
        licenseNumber: userDetail?.licenseNumber || '',
        qualification: userDetail?.qualification || '',
        availabilityDay: userDetail?.availabilityDay || '',
        fromTimeAvailability: userDetail?.fromTimeAvailability || '',
        toTimeAvailability: userDetail?.toTimeAvailability || '',
        availabilitySlots: userDetail?.availabilitySlots?.length
            ? userDetail.availabilitySlots
            : [{ from: "", to: "" }],
        signature: userDetail?.signature || '',
        sameAsContact: false,
        footerTemplate: userDetail?.footerTemplate || {
            name: '',
            role: '',
            qualification: '',
            license: '',
            specialization: '',
            availability: '',
            hprId: ''
        },
        showFooterPreview: userDetail?.showFooterPreview || false,
    })

    const toMinutes = (time: string): number => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
    };

    const getValidationSchema = (role) => {
        return (
            Yup.object().shape({
                name: Yup.string().required(Name.validation.required),
                contactNumberCountryCode: Yup.string(),
                whatsappCountryCode: Yup.string(),
                empId: Yup.string(),
                role: Yup.string().required(Role.validation.required),
                hospitalName: Yup.string(),
                hprid: Yup.string(),
                gender: Yup.string(),
                email: Yup.string().email(EMAIL.validation.valid).required(EMAIL.validation.required).test('no-uppercase', EMAIL.validation.invalidCaps, value => {
                    return !value || value === value.toLowerCase();
                }),
                contactNumber: Yup.string().matches(phoneNumberRegex, CONTACT_NUMBER.validation.valid).required(CONTACT_NUMBER.validation.valid),
                dob: Yup.date().required(DOB.validation.required).nullable()
                    .test('isValid', 'Date of birth is required', (value) => value && dayjs(value).isValid()),
                department: Yup.string(),
                specialization: role === _roleType.radiologistAdmin ? Yup.array() : role === 'Government' ? Yup.array() : Yup.array().required(SPECIALITY.validation.required).test('is-valid', SPECIALITY.validation.required, (value) => {
                    return value && value.length > 0;
                }),
                yearsOfExperience: Yup.string(),
                whatsappNumber: Yup.string().matches(phoneNumberRegex, CONTACT_NUMBER.validation.valid),
                licenseNumber: role === 'Front Desk' || role === 'Admin' || role === 'Government' ? Yup.string() : Yup.string().required(LICENSE_NUMBER.validation.required),
                qualification: (_roleType?.radiologist === role || _roleType?.pathologist === role || _roleType?.radiologistAdmin === role || _roleType?.pathologistAdmin === role) ? Yup.string().required('Qualification is required') : Yup.string(),
                availabilityDay: role === 'Front Desk' || role === 'Admin' || role === 'Government' || role === _roleType.radiologistAdmin ? Yup.string() : Yup.string().required(DAY.validation.required),
                availabilitySlots: Yup.array()
                    .of(
                        Yup.object().shape({
                        from: Yup.string().required("From Time is required"),
                        to: Yup.string()
                            .required("To Time is required")
                            .test(
                            "is-greater",
                            "To time must be later than From time",
                            function (to) {
                                const { from } = this.parent;
                                if (!from || !to) return true;

                                return toMinutes(to) > toMinutes(from);
                            }
                            ),
                        })
                    )
                    .min(1, "At least one time slot is required"),
                signature: role === 'Front Desk' ? Yup.string() : Yup.string().required(SIGNATURE.validation.required),
                sameAsContact: Yup.boolean()
            })
        )
    }

    const [password, setPassword] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })

    const dispatch = useDispatch<AppDispatch>();
    const {
        status: passwordStatus,
        msg: passwordMsg,
        result: passwordData,
        loading: passwordDataLoading,
        error: passwordError
    } = useSelector((state: RootState) => state?.changePasswordResponse);
    const {
        status: updateProfileStatus,
        msg: updateProfileMsg,
        result: updateProfileData,
        loading: updateProfileDataLoading
    } = useSelector((state: RootState) => state?.updateProfileResponse);

    const handlePasswordChange = async (key, values) => {
        setPassword({ ...password, [key]: values });
    }
    useEffect(() => {
        if (imageResponse?.status === "SUCCESS") {
            if (folderName === 'profile') {
                setProfileImage(imageResponse?.result);
            } else {
                setSignatureImage(imageResponse?.result);
            }
        }
    }, [imageResponse, folderName]);
    useEffect(() => {
        if (updateProfileStatus === 'SUCCESS') {
            setSuccessModal(true);
            setTitle("Profile updated successfully.");
            setTimeout(() => {
                navigate(`/user/details/${userDetail?._id}`);
                dispatch(resetUpdateProfileStatus());
            }, 500);
        }
    }, [updateProfileStatus]);

    useEffect(() => {
        if (passwordStatus === 'SUCCESS') {
            setSuccessModal(true);
            setTitle(passwordMsg);
            setTimeout(() => {
                dispatch(resetChangePasswordStatus());
            }, 500);
        }
    }, [passwordStatus]);

    const handlePasswordSubmit = async () => {
        setConfirmationModal(false)
        const { currentPassword, newPassword, confirmPassword } = password;
        if (!currentPassword) {
            setErrorMessage('Current password is required.');
            return;
        }
        if (!newPassword) {
            setErrorMessage('New password is required.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErrorMessage('New password and confirm password do not match.');
            return;
        }
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');

        const payload = {
            hospitalId: hospitalId,
            userId: userId,
            currentPassword: currentPassword,
            newPassword: newPassword,
            confirmPassword: confirmPassword
        }

        const response = await dispatch(changePasswordApi(payload));

        if (response?.meta?.requestStatus === 'fulfilled') {
            if (response?.payload?.status === "SUCCESS") {
                setPassword({ ...password, currentPassword: '', newPassword: '', confirmPassword: '' });
                setErrorMessage('');
            } else if (response?.payload?.status === "ERROR") {
                setErrorMessage(response?.payload?.msg);
            }
        } else if (response?.error?.message) {
            setErrorMessage(response.error.message);
        }
    }

    const onDrop = useCallback((acceptedFiles, setFieldValue) => {
        const file = acceptedFiles[0];
        uploadSignatureFile(file, setFieldValue);
    }, []);

    const uploadSignatureFile = (file, setFieldValue) => {
        if (file) {
            setFolderName("signature");
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folderName', "signature");

            dispatch(updateProfileImage(formData))
            .unwrap()
            .then((result) => {
                const signatureUrl = result?.result || '';
                setFieldValue('signature', signatureUrl);
                setSignatureImage(signatureUrl);
            })
            .catch((error) => {
                console.error('Error uploading file:', error);
            });
        }
    };

    const fileInputRef = useRef(null);

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>, folderPath) => {
        const file = event.target.files?.[0];
        const folderName = folderPath;
        setFolderName(folderName)

        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('folderName', folderName);

                dispatch(updateProfileImage(formData))
                    .unwrap()
                    .then((result) => {
                        console.log('File uploaded successfully:', result);
                    })
                    .catch((error) => {
                        console.error('Error uploading file:', error);
                    });

            };
            reader.readAsDataURL(file);
        }
    };

    const fetchCommonData = async () => {
        try {
            const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
            if (commonData?.status) {
                setRoleList(commonData?.result?.roleList);
                setCountryCode(commonData?.result?.countryCode);
                setGenderOption(commonData?.result?.gender);
                setDepartment(commonData?.result?.department);
                setSpecialitities(
                    commonData?.result?.department?.map(item =>
                        item === "Multi specality hospital"
                            ? { value: "Multispeciality Hospital", label: "Multispeciality Hospital" }
                            : { value: item, label: item }
                    )
                );
                setDays(commonData?.result?.daysOfWeek);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleSaveForm = async (values) => {
        const formData = {
            ...values,
            profilePic: profileImage
        }
        const response = await dispatch(updateProfileApi(formData));

        if (response?.meta?.requestStatus === 'fulfilled') {
            if (response?.payload?.status === "SUCCESS") {
            } else if (response?.payload?.status === "ERROR") {
            }
        } else if (response?.error?.message) {
            setErrorMessage(response.error.message);
        }
    }

    useEffect(() => {
        fetchCommonData()
        dispatch(getBillingGeneralSettings());
        if (userDetail && Object.keys(userDetail).length > 0) {
            setImage(userDetail?.hospitalDetail?.hospitalLogo);
            setPreview(userDetail?.signature);
        }
    }, [dispatch])

    useEffect(() => {
    if (billingGeneralSettingsData?.status === 'success' && billingGeneralSettingsData?.result?.currency) {
        const newCountryCode = getCurrencyContact(billingGeneralSettingsData.result.currency);
        setSelectedCountryCode(newCountryCode);
    }
    }, [billingGeneralSettingsData]);

    const base64ToBlob = (base64, type = 'image/png') => {
        const byteCharacters = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type });
    };
    const saveSignature = (signatureData: string, setFieldValue: (field: string, value: any) => void) => {
        setOpenSignaturePad(false);
        const blob = base64ToBlob(signatureData);
        const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
        setFieldValue('signature', signatureData);
        uploadSignatureFile(file, setFieldValue);
    };

    const openSignaturePad = () => {
        setOpenSignaturePad(true)
    }

    const buildAvailabilityString = (
        rawDays: string,
        slots: Array<{ from?: string; to?: string }>,
        fromTime?: string,
        toTime?: string
    ) => {
        const daysPart = (rawDays || "").trim();
        const slotParts = (slots || [])
        .filter(s => s?.from && s?.to)
        .map((s, idx) => `Timings: ${formatTime(s.from, true)} - ${formatTime(s.to, true)}`);

        const fallbackSlot =
        slotParts.length === 0 && fromTime && toTime
            ? [`Timings: ${formatTime(fromTime, true)} - ${formatTime(toTime, true)}`]
            : [];

        const timeLabel = [...slotParts, ...fallbackSlot].join(", ");

        if (daysPart && timeLabel) return `${daysPart}, ${timeLabel}`;
        if (daysPart) return daysPart;
        return timeLabel;
    };

    return (
        <>
            <div className='page page__full' style={
                isDarkMode ? {background: '#090c29'} : customStyle.mainBackground
            }>
                <Header isBack={true} title={"Edit Profile"}></Header>
                <div className='page__body'>
                    <div className='details'>
                        <div className='form form--innersec'>
                            <Formik
                                initialValues={formInitialValue}
                                enableReinitialize={true}
                                validationSchema={Yup.lazy((values) => getValidationSchema(values.role))}
                                onSubmit={handleSaveForm}
                            >
                                {({
                                    errors,
                                    values,
                                    handleChange,
                                    handleBlur,
                                    touched,
                                    setFieldValue,
                                    setFieldTouched,
                                }) => {
                                    useEffect(() => {
                                        setFieldValue('contactNumberCountryCode', selectedCountryCode);
                                        setFieldValue('whatsappCountryCode', selectedCountryCode);
                                    }, [selectedCountryCode]);
                                    console.log("values", errors);

                                    const handleEditableField = (e, field) => {
                                        setFieldValue(field, e.target.textContent.trim());
                                    };

                                    useEffect(() => {
                                        if (values.showFooterPreview && values.footerTemplate) {
                                            if (editableNameRef.current) {
                                                const nameText = values.footerTemplate.name
                                                    ? `${capitalizeWords(values.footerTemplate.name)}${values.footerTemplate.qualification ? ` (${values.footerTemplate.qualification})` : ''}`
                                                    : '';
                                                editableNameRef.current.textContent = nameText || '\u00A0';
                                            }
                                            if (editableRef.current) {
                                                const { editable } = getRoleParts(values.footerTemplate.role || '');
                                                editableRef.current.textContent = editable || '\u00A0';
                                                setLocalEditableRole(editable || '');
                                            }
                                        }
                                    }, [values.footerTemplate, values.showFooterPreview]);

                                    const getRoleParts = (role) => {
                                        if (!role) return { prefix: '', fixed: '', suffix: '' };
                                        const radMatch = role.match(/radiologist\b/i);
                                        const pathMatch = role.match(/pathologist\b/i);
                                        let selectedMatch = null;
                                        if (radMatch && pathMatch) {
                                            selectedMatch = radMatch.index < pathMatch.index ? radMatch : pathMatch;
                                        } else {
                                            selectedMatch = radMatch || pathMatch;
                                        }
                                        if (selectedMatch) {
                                            const index = selectedMatch.index;
                                            const fixed = selectedMatch[0];
                                            const prefix = role.substring(0, index).trim();
                                            const suffix = role.substring(index + fixed.length).trim();
                                            return { prefix, fixed, suffix };
                                        } else {
                                            return { prefix: '', fixed: '', suffix: role.trim() };
                                        }
                                    };

                                    const { prefix, fixed, suffix } = getRoleParts(values.footerTemplate.role);
                                    const [localPrefix, setLocalPrefix] = useState(prefix);
                                    const [localSuffix, setLocalSuffix] = useState(suffix);
                                    const prefixRef = useRef(null);
                                    const suffixRef = useRef(null);

                                    useEffect(() => {
                                        const { prefix: freshPrefix, suffix: freshSuffix } = getRoleParts(values.footerTemplate.role);
                                        setLocalPrefix(freshPrefix);
                                        setLocalSuffix(freshSuffix);
                                        if (prefixRef.current) {
                                            prefixRef.current.textContent = freshPrefix || '';
                                        }
                                        if (suffixRef.current) {
                                            suffixRef.current.textContent = freshSuffix || '';
                                        }
                                    }, [values.footerTemplate.role]);

                                    const removeFooterField = (field) => {
                                        setFieldValue(`footerTemplate.${field}`, '');
                                    };

                                    useEffect(() => {
                                        if (!values.showFooterPreview) return;
                                        const rawDays =
                                        typeof values.availabilityDay === "string"
                                            ? values.availabilityDay
                                            : Array.isArray(values.availabilityDay)
                                            ? values.availabilityDay.join(", ")
                                            : "";
                                        const availabilityString = buildAvailabilityString(
                                        rawDays,
                                        values.availabilitySlots,
                                        values.fromTimeAvailability,
                                        values.toTimeAvailability
                                        );
                                        setFieldValue('footerTemplate.availability', availabilityString);
                                    }, [
                                        values.showFooterPreview,
                                        values.availabilityDay,
                                        values.availabilitySlots,
                                        values.fromTimeAvailability,
                                        values.toTimeAvailability,
                                    ]);

                                    return (
                                    <Form>
                                        <section className='form__section'>
                                            <h1 className='form__section__title'> Profile Details</h1>
                                            <div className="formRow">
                                                <div className="formCol">
                                                    <div className="form__Field">
                                                        <label className="form__uploadProfile" htmlFor="uploadProfile">
                                                            {image && <img className="form__uploadProfile__picture" src={image} alt="" />}
                                                            <img className="form__uploadProfile__picture" src={ICONS.defaultUserIcon} alt="" />
                                                            <span className="form__uploadProfile__overlay">
                                                                <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M15.4782 12.1589C15.4782 12.5125 15.3377 12.8516 15.0877 13.1017C14.8376 13.3517 14.4985 13.4922 14.1449 13.4922H2.14486C1.79124 13.4922 1.4521 13.3517 1.20205 13.1017C0.951999 12.8516 0.811523 12.5125 0.811523 12.1589V4.82552C0.811523 4.4719 0.951999 4.13276 1.20205 3.88271C1.4521 3.63266 1.79124 3.49219 2.14486 3.49219H4.81152L6.14486 1.49219H10.1449L11.4782 3.49219H14.1449C14.4985 3.49219 14.8376 3.63266 15.0877 3.88271C15.3377 4.13276 15.4782 4.4719 15.4782 4.82552V12.1589Z" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
                                                                    <path d="M8.14486 10.8255C9.61762 10.8255 10.8115 9.63161 10.8115 8.15885C10.8115 6.68609 9.61762 5.49219 8.14486 5.49219C6.6721 5.49219 5.47819 6.68609 5.47819 8.15885C5.47819 9.63161 6.6721 10.8255 8.14486 10.8255Z" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
                                                                </svg>
                                                            </span>
                                                        </label>
                                                        <input type="file" id="uploadProfile" className="form__uploadfiled" ref={fileInputRef} accept=".jpg, .jpeg, .png"
                                                            onChange={(e) => handleImageChange(e, 'profile')} />
                                                    </div>
                                                </div>
                                            </div>
                                            {role === _roleType?.government ? (
                                                <>
                                                    <div className='formRow formRow__4'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='name'>
                                                                    Name
                                                                    <span className="required-asterisk">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="name"
                                                                    name="name"
                                                                    placeholder="Name"
                                                                    className='form__input form__input--sm disabled-field'
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.name', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.name}
                                                                    autoComplete='off'
                                                                    disabled
                                                                />
                                                                <ErrorMessage name="name" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label">
                                                                    User Role<span className="required-asterisk">*</span>
                                                                </label>
                                                                <select
                                                                    id="role"
                                                                    name="role"
                                                                    className="form__select form__select--sm disabled-field"
                                                                    disabled
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.role', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.role}
                                                                    autoComplete='off'
                                                                    style={customStyle.select}
                                                                >
                                                                    {userDetail?.role ?
                                                                        <option value={userDetail?.role}>{userDetail?.role}</option> :
                                                                        <option value="">Select</option>
                                                                    }
                                                                    {roleList.map((option) => (
                                                                        <option key={option} value={option}>
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <ErrorMessage name="role" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='hospitalName'>
                                                                    Hospital Name
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className='form__input form__input--sm disabled-field'
                                                                    id="hospitalName"
                                                                    name="hospitalName"
                                                                    disabled
                                                                    placeholder="Hospital Name"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.hospitalName}
                                                                    autoComplete='off'
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='gender'>Gender</label>
                                                                <div className="form__radio">
                                                                    {genderOptions.map((option) => (
                                                                        <label key={option.value} className="flex items-center mr-8 custom-radio-container">
                                                                            <input
                                                                                type="radio"
                                                                                name="gender"
                                                                                value={option.value}
                                                                                checked={option.value === values?.gender}
                                                                                onChange={handleChange}
                                                                                className="form__radio__input"
                                                                            />
                                                                            <span className="form__radio__label">{option.label}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__4'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" >
                                                                    Email ID<span className="required-asterisk">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="email"
                                                                    name="email"
                                                                    placeholder="Email ID"
                                                                    disabled
                                                                    className='form__input form__input--sm disabled-field'
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.email}
                                                                    autoComplete='off'
                                                                />
                                                                <ErrorMessage name="email" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label">
                                                                    Date of Birth<span className="required-asterisk">*</span>
                                                                </label>
                                                                <div className="form__date-container">
                                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                        <Field name="dob" style={{ width: '100%' }}>
                                                                            {({ field }) => (
                                                                                <DatePicker
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        '& .MuiInputBase-root': {
                                                                                            backgroundColor: '#F6FFFF',
                                                                                            borderRadius: '2rem'
                                                                                        },
                                                                                        '& .MuiInputBase-input': {
                                                                                            padding: '10px',
                                                                                        },
                                                                                    }}
                                                                                    {...field}
                                                                                    value={field.value || null}
                                                                                    format="DD/MM/YYYY"
                                                                                    maxDate={dayjs()}
                                                                                    onChange={(newValue) => {
                                                                                        setFieldValue(field.name, newValue);
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            error={touched.date && Boolean(errors.date)}
                                                                                            helperText={touched.date && errors.date}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                    </LocalizationProvider>
                                                                </div>
                                                                <ErrorMessage name="dob" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='name'>
                                                                    Contact Number<span className="required-asterisk">*</span>
                                                                </label>
                                                                <div className='form__groupField form__groupField--phonenumber'>
                                                                    <select className="form__select form__select--sm"
                                                                        style={customStyle.select}
                                                                        id="contactNumberCountryCode"
                                                                        name="contactNumberCountryCode"
                                                                        value={values.contactNumberCountryCode}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            if (values.sameAsContact) {
                                                                                setFieldValue('whatsappCountryCode', e.target.value);
                                                                                setFieldValue('whatsappNumber', values?.contactNumber);
                                                                            }
                                                                        }}>
                                                                        {countryCode.map((option) => (
                                                                            <option
                                                                                key={option}
                                                                                value={option}
                                                                                disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                            >
                                                                                {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input type="text"
                                                                        className="form__input form__input--sm"
                                                                        id="contactNumber"
                                                                        name="contactNumber"
                                                                        placeholder="Contact Number"
                                                                        maxLength={10}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            if (values.sameAsContact) {
                                                                                setFieldValue('whatsappNumber', e.target.value);
                                                                                setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode);
                                                                            }
                                                                        }}
                                                                        onInput={(e) => {
                                                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                            handleChange(e);
                                                                        }}
                                                                        pattern="[0-9]*"
                                                                        onBlur={handleBlur}
                                                                        value={values.contactNumber}
                                                                        autoComplete='off'>
                                                                    </input>
                                                                </div>
                                                                <ErrorMessage name="contactNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='whatsappNumber'>Whatsapp Number</label>
                                                                <div className="form__groupField form__groupField--phonenumber">
                                                                    <select
                                                                        className="form__select form__select--sm"
                                                                        style={customStyle.select}
                                                                        id="whatsappCountryCode"
                                                                        name="whatsappCountryCode"
                                                                        value={values.whatsappCountryCode}
                                                                        onChange={handleChange}
                                                                    >
                                                                        {countryCode.map((option) => (
                                                                            <option
                                                                                key={option}
                                                                                value={option}
                                                                                disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                            >
                                                                                {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input
                                                                        type="text"
                                                                        className="form__input form__input--sm"
                                                                        id="whatsappNumber"
                                                                        name="whatsappNumber"
                                                                        placeholder="Whatsapp Number"
                                                                        maxLength={10}
                                                                        onChange={handleChange}
                                                                        onInput={(e) => {
                                                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                            handleChange(e);
                                                                        }}
                                                                        pattern="[0-9]*"
                                                                        onBlur={handleBlur}
                                                                        value={values.whatsappNumber}
                                                                        autoComplete='off'>
                                                                    </input>
                                                                </div>
                                                                <div className="form__checkboxSubField">
                                                                    <div className="form__checkbox">
                                                                        <div className="form__checkbox__option m_0">
                                                                            <input className="form__checkbox__input" id='sameAsContact'
                                                                                checked={values.sameAsContact} type="checkbox" name="sameAsContact" value="sameAsContact" onChange={(e) => {
                                                                                    setFieldValue('sameAsContact', e.target.checked);
                                                                                    if (e.target.checked) {
                                                                                        setFieldValue('whatsappNumber', values.contactNumber);
                                                                                        setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode)
                                                                                    }
                                                                                }} />
                                                                            <label htmlFor="sameAsContact" className="form__checkbox__label"> Same as Contact Number</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <ErrorMessage name="whatsappNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className='formRow formRow__4'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='name'>
                                                                    Name
                                                                    <span className="required-asterisk">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="name"
                                                                    name="name"
                                                                    placeholder="Name"
                                                                    className='form__input form__input--sm disabled-field'
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.name', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.name}
                                                                    autoComplete='off'
                                                                    disabled
                                                                />
                                                                <ErrorMessage name="name" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='employeeId' >
                                                                    Employee ID
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="empId"
                                                                    name="empId"
                                                                    disabled
                                                                    className='form__input form__input--sm disabled-field'
                                                                    placeholder="Employee ID"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.empId}
                                                                    autoComplete='off'
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label">
                                                                    User Role<span className="required-asterisk">*</span>
                                                                </label>
                                                                <select
                                                                    id="role"
                                                                    name="role"
                                                                    className="form__select form__select--sm disabled-field"
                                                                    disabled
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.role', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.role}
                                                                    autoComplete='off'
                                                                    style={customStyle.select}
                                                                >
                                                                    {userDetail?.role ?
                                                                        <option value={userDetail?.role}>{userDetail?.role}</option> :
                                                                        <option value="">Select</option>
                                                                    }
                                                                    {roleList.map((option) => (
                                                                        <option key={option} value={option}>
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <ErrorMessage name="role" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='hospitalName'>
                                                                    Hospital Name
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className='form__input form__input--sm disabled-field'
                                                                    id="hospitalName"
                                                                    name="hospitalName"
                                                                    disabled
                                                                    placeholder="Hospital Name"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.hospitalName}
                                                                    autoComplete='off'
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__4'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='hprId'>
                                                                    HPR ID
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className='form__input form__input--sm'
                                                                    id="hprId"
                                                                    name="hprId"
                                                                    placeholder="HPR ID"
                                                                    onChange={(e) => {
                                                                        const numericValue = e.target.value.replace(/\D/g, '');
                                                                        handleChange({
                                                                            target: {
                                                                                name: e.target.name,
                                                                                value: numericValue
                                                                            }
                                                                        });

                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.hprId', numericValue);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.hprId}
                                                                    autoComplete='off'
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='gender'>Gender</label>
                                                                <div className="form__radio">
                                                                    {genderOptions.map((option) => (
                                                                        <label key={option.value} className="flex items-center mr-8 custom-radio-container">
                                                                            <input
                                                                                type="radio"
                                                                                name="gender"
                                                                                value={option.value}
                                                                                checked={option.value === values?.gender}
                                                                                onChange={handleChange}
                                                                                className="form__radio__input"
                                                                            />
                                                                            <span className="form__radio__label">{option.label}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" >
                                                                    Email ID<span className="required-asterisk">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    id="email"
                                                                    name="email"
                                                                    placeholder="Email ID"
                                                                    disabled
                                                                    className='form__input form__input--sm disabled-field'
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.email}
                                                                    autoComplete='off'
                                                                />
                                                                <ErrorMessage name="email" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='name'>
                                                                    Contact Number<span className="required-asterisk">*</span>
                                                                </label>
                                                                <div className='form__groupField form__groupField--phonenumber'>
                                                                    <select className="form__select form__select--sm"
                                                                        style={customStyle.select}
                                                                        id="contactNumberCountryCode"
                                                                        name="contactNumberCountryCode"
                                                                        value={values.contactNumberCountryCode}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            if (values.sameAsContact) {
                                                                                setFieldValue('whatsappCountryCode', e.target.value);
                                                                                setFieldValue('whatsappNumber', values?.contactNumber);
                                                                            }
                                                                        }}>
                                                                        {countryCode.map((option) => (
                                                                            <option
                                                                                key={option}
                                                                                value={option}
                                                                                disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                            >
                                                                                {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input type="text"
                                                                        className="form__input form__input--sm"
                                                                        id="contactNumber"
                                                                        name="contactNumber"
                                                                        placeholder="Contact Number"
                                                                        maxLength={10}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            if (values.sameAsContact) {
                                                                                setFieldValue('whatsappNumber', e.target.value);
                                                                                setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode);
                                                                            }
                                                                        }}
                                                                        onInput={(e) => {
                                                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                            handleChange(e);
                                                                        }}
                                                                        pattern="[0-9]*"
                                                                        onBlur={handleBlur}
                                                                        value={values.contactNumber}
                                                                        autoComplete='off'>
                                                                    </input>
                                                                </div>
                                                                <ErrorMessage name="contactNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__4'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label">
                                                                    Date of Birth<span className="required-asterisk">*</span>
                                                                </label>
                                                                <div className="form__date-container">
                                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                        <Field name="dob" style={{ width: '100%' }}>
                                                                            {({ field }) => (
                                                                                <DatePicker
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        '& .MuiInputBase-root': {
                                                                                            backgroundColor: '#F6FFFF',
                                                                                            borderRadius: '2rem'
                                                                                        },
                                                                                        '& .MuiInputBase-input': {
                                                                                            padding: '10px',
                                                                                        },
                                                                                    }}
                                                                                    {...field}
                                                                                    value={field.value || null}
                                                                                    format="DD/MM/YYYY"
                                                                                    maxDate={dayjs()}
                                                                                    onChange={(newValue) => {
                                                                                        setFieldValue(field.name, newValue);
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            error={touched.date && Boolean(errors.date)}
                                                                                            helperText={touched.date && errors.date}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                    </LocalizationProvider>
                                                                </div>
                                                                <ErrorMessage name="dob" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className='form__label'>
                                                                    Department
                                                                </label>
                                                                <select
                                                                    id="department"
                                                                    name="department"
                                                                    className="form__select form__select--sm"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.department}
                                                                    autoComplete='off'
                                                                    style={customStyle.select}
                                                                >
                                                                    <option value="">Select</option>
                                                                    {department.map((option) => (
                                                                        <option key={option} value={option}>
                                                                            {option}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='numberOfHealthcareProfessionals'>Specialization
                                                                    <span className="required-asterisk">*</span>
                                                                </label>
                                                                <Select id='specialization'
                                                                    placeholder='Select'
                                                                    className='cutom-bg-white'
                                                                    isMulti={true}
                                                                    isClearable={false}
                                                                    isSearchable={false}
                                                                    closeMenuOnSelect={false}
                                                                    hideSelectedOptions={false}
                                                                    options={specialitites}
                                                                    value={values.specialization}
                                                                    onChange={(selectedOptions) => {
                                                                        const newSpecialties = selectedOptions ? selectedOptions.map(option => option) : [];
                                                                        setFieldValue('specialization', newSpecialties);
                                                                            if (values.showFooterPreview) {
                                                                            const joinedSpecialties = newSpecialties
                                                                                .map(opt => (typeof opt === 'object' && opt.value ? opt.value : opt))
                                                                                .filter(Boolean)
                                                                                .join(', ');

                                                                            setFieldValue('footerTemplate.specialization', joinedSpecialties);
                                                                        }
                                                                    }}
                                                                ></Select>
                                                                <ErrorMessage name="specialization" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='numberOfHealthcareProfessionals'>Years of Experience</label>
                                                                <input
                                                                    type="number"
                                                                    className="form__input form__input--sm"
                                                                    id="yearsOfExperience"
                                                                    name="yearsOfExperience"
                                                                    placeholder="--"
                                                                    min='0'
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.yearsOfExperience}
                                                                    autoComplete='off'
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__3'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='whatsappNumber'>Whatsapp Number</label>
                                                                <div className="form__groupField form__groupField--phonenumber">
                                                                    <select
                                                                        className="form__select form__select--sm"
                                                                        style={customStyle.select}
                                                                        id="whatsappCountryCode"
                                                                        name="whatsappCountryCode"
                                                                        value={values.whatsappCountryCode}
                                                                        onChange={handleChange}
                                                                    >
                                                                        {countryCode.map((option) => (
                                                                            <option
                                                                                key={option}
                                                                                value={option}
                                                                                disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                            >
                                                                                {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input
                                                                        type="text"
                                                                        className="form__input form__input--sm"
                                                                        id="whatsappNumber"
                                                                        name="whatsappNumber"
                                                                        placeholder="Whatsapp Number"
                                                                        maxLength={10}
                                                                        onChange={handleChange}
                                                                        onInput={(e) => {
                                                                            e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                                                            handleChange(e);
                                                                        }}
                                                                        pattern="[0-9]*"
                                                                        onBlur={handleBlur}
                                                                        value={values.whatsappNumber}
                                                                        autoComplete='off'>
                                                                    </input>
                                                                </div>
                                                                <div className="form__checkboxSubField">
                                                                    <div className="form__checkbox">
                                                                        <div className="form__checkbox__option m_0">
                                                                            <input className="form__checkbox__input" id='sameAsContact'
                                                                                checked={values.sameAsContact} type="checkbox" name="sameAsContact" value="sameAsContact" onChange={(e) => {
                                                                                    setFieldValue('sameAsContact', e.target.checked);
                                                                                    if (e.target.checked) {
                                                                                        setFieldValue('whatsappNumber', values.contactNumber);
                                                                                        setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode)
                                                                                    }
                                                                                }} />
                                                                            <label htmlFor="sameAsContact" className="form__checkbox__label"> Same as Contact Number</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <ErrorMessage name="whatsappNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='numberOfHealthcareProfessionals'>
                                                                    State Medical Council Registration License Number
                                                                    {userDetail?.role !== "Front Desk" && userDetail?.role !== "Admin" && <span className="required-asterisk">*</span>}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form__input form__input--sm"
                                                                    id="licenseNumber"
                                                                    name="licenseNumber"
                                                                    placeholder="License Number"
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.license', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.licenseNumber}
                                                                    autoComplete='off'
                                                                />
                                                                <ErrorMessage name="licenseNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='qualification'>
                                                                    Qualification
                                                                    {(userDetail?.role === _roleType?.radiologist || userDetail?.role === _roleType?.pathologist || userDetail?.role === _roleType?.radiologistAdmin || userDetail?.role === _roleType?.pathologistAdmin) && <span className="required-asterisk">*</span>}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form__input form__input--sm"
                                                                    id="qualification"
                                                                    name="qualification"
                                                                    placeholder="Qualification"
                                                                    onChange={(e) => {
                                                                        handleChange(e);
                                                                        if (values.showFooterPreview) {
                                                                            setFieldValue('footerTemplate.qualification', e.target.value);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                    value={values.qualification}
                                                                    autoComplete='off'
                                                                />
                                                                <ErrorMessage name="qualification" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__3'>
                                                        <FieldArray name="availabilitySlots">
                                                        {({ push, remove }) => (
                                                            <div>
                                                            <label className="form__label">
                                                                Availability Time
                                                                <span className="required-asterisk">*</span>
                                                                <button
                                                                type="button"
                                                                onClick={() => push({ from: "", to: "" })}
                                                                className="text-lg text-[#51A196] ml-2"
                                                                >
                                                                + Add more
                                                                </button>
                                                            </label>

                                                            {values.availabilitySlots.map((slot, index) => (
                                                                <div className="formRow flex items-center gap-3 mb-3" key={index}>
                                                                <div className="formRow formRow__2">
                                                                    <div className="formCol">
                                                                    <div className="flex items-center justify-between">
                                                                        <label
                                                                        className="form__label"
                                                                        htmlFor={`availabilitySlots.${index}.from`}
                                                                        >
                                                                        From Time
                                                                        </label>

                                                                        {index > 0 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            className="text-red-500 text-base hover:underline ml-2"
                                                                        >
                                                                            Remove
                                                                        </button>
                                                                        )}
                                                                    </div>

                                                                    <input
                                                                        type="time"
                                                                        id={`availabilitySlots.${index}.from`}
                                                                        name={`availabilitySlots.${index}.from`}
                                                                        value={slot.from}
                                                                        onChange={(e) =>
                                                                        setFieldValue(`availabilitySlots.${index}.from`, e.target.value)
                                                                        }
                                                                        className="form__date form__date--sm"
                                                                        style={customStyle.time}
                                                                    />

                                                                    <ErrorMessage
                                                                        name={`availabilitySlots.${index}.from`}
                                                                        component="div"
                                                                        className="text-red-500 text-sm font-medium"
                                                                    />
                                                                    </div>

                                                                    <div className="formCol">
                                                                    <label
                                                                        className="form__label"
                                                                        htmlFor={`availabilitySlots.${index}.to`}
                                                                    >
                                                                        To Time
                                                                    </label>
                                                                    <input
                                                                        type="time"
                                                                        id={`availabilitySlots.${index}.to`}
                                                                        name={`availabilitySlots.${index}.to`}
                                                                        value={slot.to}
                                                                        onChange={(e) =>
                                                                        setFieldValue(`availabilitySlots.${index}.to`, e.target.value)
                                                                        }
                                                                        className="form__date form__date--sm"
                                                                        style={customStyle.time}
                                                                    />

                                                                    <ErrorMessage
                                                                        name={`availabilitySlots.${index}.to`}
                                                                        component="div"
                                                                        className="text-red-500 text-sm font-medium"
                                                                    />
                                                                    </div>
                                                                </div>
                                                                </div>
                                                            ))}
                                                            </div>
                                                        )}
                                                        </FieldArray>


                                                        <div className="formCol mt-6">
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='day'>
                                                                    Day
                                                                    {userDetail?.role !== "Front Desk" && userDetail?.role !== "Admin" && <span className="required-asterisk">*</span>}
                                                                </label>
                                                                <Field
                                                                    id="availabilityDay"
                                                                    name="availabilityDay"
                                                                    type="text"
                                                                    placeholder="Enter days (e.g., Mon, Tue, Wed)"
                                                                    className="form__input form__input--sm"
                                                                    onChange={(e) => {
                                                                        const raw = e.target.value;
                                                                        const sanitized = raw.replace(/[0-9]/g, "");
                                                                        setFieldValue("availabilityDay", sanitized);

                                                                        if (values.showFooterPreview) {
                                                                            const availabilityString = buildAvailabilityString(
                                                                            sanitized,
                                                                            values.availabilitySlots,
                                                                            values.fromTimeAvailability,
                                                                            values.toTimeAvailability
                                                                            );
                                                                            setFieldValue("footerTemplate.availability", availabilityString);
                                                                        }
                                                                    }}
                                                                    onBlur={handleBlur}
                                                                />
                                                                <ErrorMessage name="availabilityDay" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </section>
                                        <section className='form__section'>
                                            <h1 className='form__section__title'>Change Password</h1>
                                            <div className='formRow formRow__4'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__Label" htmlFor='currentPassword'>
                                                            Current Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            id="currentPassword"
                                                            name="currentPassword"
                                                            placeholder="Current Password"
                                                            className="form__input form__input--sm"
                                                            value={password.currentPassword}
                                                            onChange={(event) => handlePasswordChange('currentPassword', event.target.value)}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__Label" htmlFor='newPassword'>
                                                            New Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            id="newPassword"
                                                            name="newPassword"
                                                            placeholder="New Password"
                                                            className="form__input form__input--sm"
                                                            value={password.newPassword}
                                                            onChange={(event) => handlePasswordChange('newPassword', event.target.value)}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className='form__Label' htmlFor='confirmPassword'>
                                                            Confirm Password
                                                        </label>
                                                        <input
                                                            type="password"
                                                            id="confirmPassword"
                                                            name="confirmPassword"
                                                            placeholder="Confirm Password"
                                                            className="form__input form__input--sm"
                                                            value={password.confirmPassword}
                                                            onChange={(event) => handlePasswordChange('confirmPassword', event.target.value)}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label htmlFor="">&nbsp;</label>
                                                        <span
                                                            className='btn btn__primary'
                                                            onClick={() => {
                                                                setTitle("Update Password")
                                                                setConfirmationText("Are you sure you want update your password?")
                                                                setConfirmationModal(true)
                                                            }}
                                                        >
                                                            Update Password
                                                        </span>
                                                    </div>
                                                </div>
                                                {errorMessage && <span className='text-error text-xs' style={{ color: 'red' }}>{errorMessage}</span>}
                                            </div>
                                        </section>
                                        <section className='form__section'>
                                            <div className="formRow">
                                                <div className="formCol">
                                                    <div className="form__Field">
                                                        <label className='form__Label' htmlFor='signature'>
                                                            Add/Upload Signature
                                                            {userDetail?.role !== "Front Desk" && <span className="required-asterisk">*</span>}
                                                        </label>
                                                        <div className='cursor-pointer relative'>
                                                            <SignatureDropzone onDrop={(acceptedFiles) => onDrop(acceptedFiles, setFieldValue)} preview={values?.signature} accept={{ 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] }} />
                                                        </div>
                                                        <div>
                                                            <p className="addSignatureNote" style={{ color: isDarkMode ? '#ffffff' : '#a00000' }}> Note: Please use a black pen and white paper to sign </p>
                                                            <p className='addSignatureDiv -mt-2'>Or</p>
                                                        </div>
                                                        <div className='addSignatureDiv'>
                                                            <button className="btn btn__white" type="button" onClick={openSignaturePad}> Add Signature</button>
                                                        </div>
                                                        <ErrorMessage name="signature" component="div" className="error text-error" />
                                                    </div>
                                                </div>
                                            </div>
                                            {(_roleType?.radiologist === role || _roleType?.pathologist === role || _roleType?.radiologistAdmin === role || _roleType?.pathologistAdmin === role) && (
                                                <section className='form__section' style={{ marginBottom: '20px' }}>
                                                    <h1 className='form__section__title' style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>Signature Footer Template</h1>
                                                    <div className='form__Field mb-4'>
                                                        <label className="form__label">Select Template</label>
                                                        <div className="flex items-center gap-4">
                                                            <select
                                                                className='form__select form__select--sm'
                                                                onChange={(e) => {
                                                                    const selected = e.target.value;
                                                                    if (selected === 'default') {
                                                                        const specializationString = values.specialization
                                                                        ? values.specialization
                                                                            .map(opt => (typeof opt === 'object' && opt?.value ? opt.value : opt))
                                                                            .filter(Boolean)
                                                                            .join(', ')
                                                                        : '';

                                                                        const rawDays =
                                                                        typeof values.availabilityDay === 'string'
                                                                            ? values.availabilityDay
                                                                            : Array.isArray(values.availabilityDay)
                                                                            ? values.availabilityDay.join(', ')
                                                                            : '';

                                                                        const availabilityString = buildAvailabilityString(
                                                                        rawDays,
                                                                        values.availabilitySlots,
                                                                        values.fromTimeAvailability,
                                                                        values.toTimeAvailability
                                                                        );

                                                                        setFieldValue('footerTemplate', {
                                                                        name: values.name || '',
                                                                        role: values.role || '',
                                                                        qualification: values.qualification || '',
                                                                        license: values.licenseNumber || '',
                                                                        specialization: specializationString,
                                                                        availability: availabilityString,
                                                                        hprId: values?.hprId,
                                                                        });
                                                                        setFieldValue('showFooterPreview', true);
                                                                    } else {
                                                                        setFieldValue('footerTemplate', {
                                                                        name: '',
                                                                        role: '',
                                                                        qualification: '',
                                                                        license: '',
                                                                        specialization: '',
                                                                        availability: '',
                                                                        hprId: '',
                                                                        });
                                                                        setFieldValue('showFooterPreview', false);
                                                                    }
                                                                }}
                                                                value={values.footerTemplate && values.showFooterPreview ? 'default' : ''}
                                                                style={{ color: '#000000', width: '250px', backgroundColor: '#ffffff', borderColor: '#cccccc' }}
                                                            >
                                                                <option value="" style={{ color: '#000000' }}>Select</option>
                                                                <option value="default" style={{ color: '#000000' }}>Default Template</option>
                                                            </select>
                                                            {values.showFooterPreview && (
                                                                <button
                                                                    type="button"
                                                                    className='btn btn__white'
                                                                    style={{ padding: '8px 16px', fontSize: '14px' }}
                                                                    onClick={() => {
                                                                        const specializationString = values.specialization
                                                                            ? values.specialization
                                                                                .map(opt => (typeof opt === 'object' && opt?.value ? opt.value : opt))
                                                                                .filter(Boolean)
                                                                                .join(', ')
                                                                            : '';
                                                                        const availabilityString =
                                                                        values.availabilityDay && values.availabilitySlots?.length
                                                                            ? `${formatDays(
                                                                                typeof values.availabilityDay === "string"
                                                                                ? values.availabilityDay.split(",").map(d => d.trim())
                                                                                : values.availabilityDay
                                                                            )}, ` +
                                                                            values.availabilitySlots
                                                                                .map((slot, i) => {
                                                                                const from = slot.from ? formatTime(slot.from, true) : "";
                                                                                const to = slot.to ? formatTime(slot.to, true) : "";
                                                                                return `Timings: ${from} - ${to}`;
                                                                                })
                                                                                .join(", ")
                                                                            : "";
                                                                        const newFooterTemplate = {
                                                                            name: values.name || '',
                                                                            role: values.role || '',
                                                                            qualification: values.qualification || '',
                                                                            license: values.licenseNumber || '',
                                                                            specialization: specializationString,
                                                                            availability: availabilityString,
                                                                            hprId: values.hprId,
                                                                            authorizedOn: userDetail?.authorizedOn || '',
                                                                        };
                                                                        setFieldValue('footerTemplate', newFooterTemplate);
                                                                        setFieldValue('showFooterPreview', true);
                                                                        if (editableNameRef.current) {
                                                                            const nameText = values.name ? `${capitalizeWords(values.name)}${values.qualification ? ` (${values.qualification})` : ''}` : '';
                                                                            editableNameRef.current.textContent = nameText || '\u00A0';
                                                                        }
                                                                        if (editableRef.current) {
                                                                            const { editable } = getRoleParts(values.role || '');
                                                                            editableRef.current.textContent = editable || '\u00A0';
                                                                        }
                                                                    }}
                                                                    disabled={(() => {
                                                                        if (!values.showFooterPreview || !values.footerTemplate) return true;
                                                                        return (
                                                                            values.footerTemplate.name === values.name &&
                                                                            values.footerTemplate.role === values.role &&
                                                                            values.footerTemplate.qualification === values.qualification &&
                                                                            values.footerTemplate.license === values.licenseNumber &&
                                                                            values.footerTemplate.specialization ===
                                                                                (values.specialization
                                                                                    ? values.specialization
                                                                                        .map(opt => (typeof opt === 'object' && opt?.value ? opt.value : opt))
                                                                                        .filter(Boolean)
                                                                                        .join(', ')
                                                                                    : '') &&
                                                                            values.footerTemplate.availability ===
                                                                                (values.availabilityDay && values.availabilitySlots?.length
                                                                                    ? `${formatDays(
                                                                                        typeof values.availabilityDay === "string"
                                                                                        ? values.availabilityDay.split(",").map(d => d.trim())
                                                                                        : values.availabilityDay
                                                                                    )}, ` +
                                                                                    values.availabilitySlots
                                                                                        .map((slot, i) => {
                                                                                        const from = slot.from ? formatTime(slot.from, true) : "";
                                                                                        const to = slot.to ? formatTime(slot.to, true) : "";
                                                                                        return `Timings: ${from} - ${to}`;
                                                                                        })
                                                                                        .join(", ")
                                                                                    : '') &&
                                                                            values.footerTemplate.hprId === values.hprId &&
                                                                            values.footerTemplate.authorizedOn === (userDetail?.authorizedOn || '')
                                                                        );
                                                                    })()}
                                                                >
                                                                    Reset Template
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {values.showFooterPreview && values.footerTemplate && (
                                                        <div
                                                            className="rounded-lg border p-4 mt-2"
                                                            style={{
                                                                maxWidth: '520px',
                                                                backgroundColor: '#f8f9fa',
                                                                color: '#000000',
                                                                boxShadow: '0 1px 5px rgba(0,0,0,0.1)'
                                                            }}
                                                        >
                                                            {values.footerTemplate.name && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                            fontSize: '16px',
                                                                            color: '#FF0000',
                                                                            margin: '0',
                                                                            minHeight: '24px',
                                                                            background: 'transparent',
                                                                        }}
                                                                    >
                                                                        {`Dr. ${capitalizeWords(values.footerTemplate.name)}${values.footerTemplate.qualification ? `, ${values.footerTemplate.qualification}` : ''}`}
                                                                    </p>
                                                                </div>
                                                            )}
                                                            {(!values.footerTemplate.name || values.footerTemplate.name.trim() === '') && (
                                                                <span
                                                                    style={{
                                                                        color: 'red',
                                                                        fontSize: '16px',
                                                                        padding: '0',
                                                                    }}
                                                                >
                                                                    Name is required
                                                                </span>
                                                            )}
                                                            {values.footerTemplate.role && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p style={{ color: '#000000', fontSize: '16px', margin: '0' }}>
                                                                        <span
                                                                            ref={prefixRef}
                                                                            contentEditable={true}
                                                                            suppressContentEditableWarning={true}
                                                                            onInput={(e) => {
                                                                                setLocalPrefix(e.currentTarget.textContent.trim());
                                                                            }}
                                                                            onBlur={() => {
                                                                                const finalRole = `${localPrefix} ${fixed} ${localSuffix}`.trim();
                                                                                setFieldValue('footerTemplate.role', finalRole || '');
                                                                            }}
                                                                            onFocus={(e) => {
                                                                                const el = e.currentTarget;
                                                                                const range = document.createRange();
                                                                                range.selectNodeContents(el);
                                                                                range.collapse(false);
                                                                                const sel = window.getSelection();
                                                                                sel.removeAllRanges();
                                                                                sel.addRange(range);
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') e.preventDefault();
                                                                            }}
                                                                            style={{
                                                                                color: '#000000',
                                                                                border: 'none',
                                                                                outline: 'none',
                                                                                display: 'inline-block',
                                                                                minWidth: '20px',
                                                                                whiteSpace: 'pre-wrap',
                                                                                cursor: 'text',
                                                                            }}
                                                                        >
                                                                            {prefix}
                                                                        </span>

                                                                        {fixed && (
                                                                            <span style={{ pointerEvents: 'none', userSelect: 'none' }}>
                                                                                {' '}{fixed}{' '}
                                                                            </span>
                                                                        )}

                                                                        <span
                                                                            ref={suffixRef}
                                                                            contentEditable={fixed.toLowerCase() === 'radiologist' || fixed.toLowerCase() === 'pathologist'}
                                                                            suppressContentEditableWarning={true}
                                                                            onInput={(e) => {
                                                                                setLocalSuffix(e.currentTarget.textContent.trim());
                                                                            }}
                                                                            onBlur={() => {
                                                                                const finalRole = `${localPrefix} ${fixed} ${localSuffix}`.trim();
                                                                                setFieldValue('footerTemplate.role', finalRole || '');
                                                                            }}
                                                                            onFocus={(e) => {
                                                                                const el = e.currentTarget;
                                                                                const range = document.createRange();
                                                                                range.selectNodeContents(el);
                                                                                range.collapse(false);
                                                                                const sel = window.getSelection();
                                                                                sel.removeAllRanges();
                                                                                sel.addRange(range);
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') e.preventDefault();
                                                                            }}
                                                                            style={{
                                                                                color: '#000000',
                                                                                border: 'none',
                                                                                outline: 'none',
                                                                                display: 'inline-block',
                                                                                minWidth: '20px',
                                                                                whiteSpace: 'pre-wrap',
                                                                                cursor: (fixed.toLowerCase() === 'radiologist' || fixed.toLowerCase() === 'pathologist') ? 'text' : 'default',
                                                                            }}
                                                                        >
                                                                            {suffix}
                                                                        </span>
                                                                    </p>
                                                                    <span
                                                                        onClick={() => removeFooterField('role')}
                                                                        style={{ cursor: 'pointer', color: '#FF0000' }}
                                                                    >
                                                                        <Icon name="close" style={{ width: '16px', height: '16px' }} />
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {values.footerTemplate.license && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p style={{ fontSize: '16px', color: '#FF0000', margin: '0' }}>
                                                                        <span style={{ fontWeight: 'bold', pointerEvents: 'none' }}>License Number: </span>
                                                                        <span style={{ color: '#000000' }}>
                                                                            {values.footerTemplate.license}
                                                                        </span>
                                                                    </p>
                                                                    <span
                                                                        onClick={() => removeFooterField('license')}
                                                                        style={{ cursor: 'pointer', color: '#FF0000' }}
                                                                    >
                                                                        <Icon name="close" style={{ width: '16px', height: '16px' }} />
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {values.footerTemplate.hprId && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p style={{ fontSize: '16px', color: '#FF0000', margin: '0' }}>
                                                                        <span style={{ fontWeight: 'bold', pointerEvents: 'none' }}>HPR ID: </span>
                                                                        <span style={{ color: '#000000' }}>
                                                                            {values.footerTemplate.hprId}
                                                                        </span>
                                                                    </p>
                                                                    <span
                                                                        onClick={() => removeFooterField('hprId')}
                                                                        style={{ cursor: 'pointer', color: '#FF0000' }}
                                                                    >
                                                                        <Icon name="close" style={{ width: '16px', height: '16px' }} />
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {values.footerTemplate.specialization && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p style={{ fontSize: '16px', color: '#FF0000', margin: '0' }}>
                                                                        <span style={{ fontWeight: 'bold', pointerEvents: 'none' }}>Specialization: </span>
                                                                        <span style={{ color: '#000000' }}>
                                                                            {values.footerTemplate.specialization}
                                                                        </span>
                                                                    </p>
                                                                    <span
                                                                        onClick={() => removeFooterField('specialization')}
                                                                        style={{ cursor: 'pointer', color: '#FF0000' }}
                                                                    >
                                                                        <Icon name="close" style={{ width: '16px', height: '16px' }} />
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {values.footerTemplate.availability && (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <p style={{ fontSize: '16px', color: '#FF0000', margin: '0' }}>
                                                                        <span style={{ fontWeight: 'bold', pointerEvents: 'none' }}>Availability: </span>
                                                                        <span style={{ color: '#000000' }}>
                                                                            {values.footerTemplate.availability}
                                                                        </span>
                                                                    </p>
                                                                    <span
                                                                        onClick={() => removeFooterField('availability')}
                                                                        style={{ cursor: 'pointer', color: '#FF0000' }}
                                                                    >
                                                                        <Icon name="close" style={{ width: '16px', height: '16px' }} />
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <p className="text-base mt-2 text-gray-600" style={{ color: '#000000', border: 'none', outline: 'none' }}>
                                                                This is a system generated report. Personal signature may not be required.
                                                            </p>
                                                        </div>
                                                    )}
                                                </section>
                                            )}
                                            <div className='btn__group'>
                                                <button
                                                    className='btn btn__primary'
                                                    type="submit"
                                                    disabled={
                                                        values.showFooterPreview &&
                                                        values.footerTemplate &&
                                                        (!values.footerTemplate.name || values.footerTemplate.name.trim() === '')
                                                    }
                                                >
                                                    Update
                                                </button>
                                                <button
                                                    className='btn btn__white'
                                                    onClick={() => {
                                                        navigate(`/user/details/${userDetail?._id}`)
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </section>
                                        <SignaturePad
                                            title={title}
                                            message={confirmationText}
                                            onConfirm={(signatureData) => saveSignature(signatureData, setFieldValue)}
                                            onCancel={() => {
                                                setOpenSignaturePad(false);
                                            }}
                                            isOpen={isOpenSignaturePad}
                                        />
                                    </Form>
                                )
                            }}
                            </Formik>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmationPopup
                title={title}
                message={confirmationText}
                onConfirm={handlePasswordSubmit}
                onCancel={() => {
                    setTitle('')
                    setConfirmationText('')
                    setConfirmationModal(false)
                }}
                isOpen={confirmationModal}
            />
            <SuccessModal
                isSuccessModalOpen={successModal}
                title={title}
                handleCloseModal={() => setSuccessModal(false)}
            />
            <Loader
                isLoaderOpen={passwordDataLoading || updateProfileDataLoading}
                title={"Loading"}
            />
        </>
    )
}

export default Profile

export const SignatureDropzone = ({ onDrop, preview }) => {
    const [error, setError] = useState(null);
    const isDarkMode = useTheme();

    const handleDrop = (acceptedFiles) => {
        const maxSize = 3 * 1024 * 1024; // 3 MB
        const validFiles = [];

        setError(null);

        for (const file of acceptedFiles) {
            if (file.size <= maxSize) {
                validFiles.push(file);
            } else {
                onDrop([]); // Clear previous images
                setError(`File ${file.name} is too large. Maximum image size is 3 MB.`);
                break;
            }
        }

        if (validFiles.length > 0) {
            onDrop(validFiles);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        accept: {
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
        },
    });

    return (
        <div
            {...getRootProps()}
            style={{
                border: '2px dashed #cccccc',
                padding: '20px',
                textAlign: 'center',
                marginBottom: '10px',
                width: '100%',
                height: '150px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: isDragActive ? '#f0f0f0' : 'white',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <input {...getInputProps()} />
            <div className='text-center flex flex-col items-center justify-center'>
                {!preview ? (
                    <>
                        <Icon name="Upload_Icon" alt="appointmentPreview" />
                        <p style={{ color: isDarkMode ? '#000000' : 'inherit' }}>
                            Drag & Drop files or{' '}
                            <span className='main-theme-color underline' style={{ textDecoration: 'underline' }}>
                                Browse
                            </span>
                        </p>
                        <p style={{ color: isDarkMode ? '#000000' : 'inherit' }}>
                            Supported format:{' '}
                            <span className='main-theme-color'>JPEG, PNG</span>
                        </p>
                    </>
                ) : (
                    <img
                        src={preview}
                        alt="Signature Preview"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                )}
                {error && <p className="text-red-500">{error}</p>}
            </div>
        </div>
    );
};
